import { useMemo } from 'react';
import { SearchableSelect } from '@/components/ui/SearchableSelect';
import { useEmployeeSelectSearch } from '@/hooks/useEmployeeSelectSearch';
import type { EmployeeResponse } from '@/types/employee';

export interface EmployeeSelectProps {
  label?: string;
  value?: string;
  onChange?: (value: string) => void;
  onEmployeeChange?: (employee: EmployeeResponse | null) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  activeOnly?: boolean;
  required?: boolean;
  error?: string;
  hint?: string;
  showDetails?: boolean;
  name?: string;
}

/** Searchable employee picker — server search by code, name, or email. */
export function EmployeeSelect({
  label = 'Employee',
  value = '',
  onChange,
  onEmployeeChange,
  placeholder = 'Select employee',
  disabled,
  className,
  activeOnly = true,
  required,
  error,
  hint,
  showDetails = true,
  name,
}: EmployeeSelectProps) {
  const { options, loading, searching, onSearchQuery, getEmployee } = useEmployeeSelectSearch({
    value,
    activeOnly,
  });

  const selected = useMemo(() => (value ? getEmployee(value) : null), [getEmployee, value]);

  const handleChange = (next: string) => {
    onChange?.(next);
    onEmployeeChange?.(next ? getEmployee(next) ?? null : null);
  };

  return (
    <div className={className}>
      <SearchableSelect
        label={label}
        name={name}
        required={required}
        error={error}
        hint={hint}
        value={value}
        onChange={handleChange}
        options={options}
        placeholder={placeholder}
        disabled={disabled}
        loading={loading}
        searching={searching}
        searchPlaceholder="Search by code, name, or email…"
        emptyMessage="No employees found"
        filterLocally={false}
        onSearchQuery={onSearchQuery}
      />
      {showDetails && selected && (
        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
          {[selected.designation, selected.email, selected.roleName].filter(Boolean).join(' · ') ||
            `ID ${selected.id}`}
        </p>
      )}
    </div>
  );
}
